import { z } from "zod";

/**
 * Owner-portal shared contract (mobile gap B3).
 *
 * Wire shapes for the owner self-service reads served by
 * `apps/api/src/owner-portal/*` (`GET /owner-portal/me`,
 * `/owner-portal/finance-summary`). They replace the direct Supabase reads in
 * `useOwnerRecord` / `useOwnerFinancials` (`src/hooks/useOwnerData.ts`) for the
 * Flutter app (screens 01 and 15), and for the web when it flips.
 *
 * Shape is **snake_case**, matching the rest of `@dallali/shared`. Every object
 * is `.passthrough()` (conventions §8) so a server-side field addition never
 * breaks an older client. Money is a plain `number` (Decimal → toNumber() on
 * the server); dates are `YYYY-MM-DD` strings.
 */

// ─── Query ───────────────────────────────────────────────────────────────────

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

/** Most months the trend series may span (two years). */
export const OWNER_FINANCE_MAX_MONTHS = 24;

/**
 * `GET /owner-portal/finance-summary` query.
 * - `start_date` / `end_date` (inclusive, either optional) bound the totals;
 *   both omitted → all time.
 * - `months` (1–24, default 12) is the length of the trailing monthly series,
 *   which always ends at the current Riyadh month and ignores the range (the
 *   web's trend chart behaves the same way).
 */
export const ownerPortalFinanceQuerySchema = z
  .object({
    start_date: dateOnly.optional(),
    end_date: dateOnly.optional(),
    months: z.coerce.number().int().min(1).max(OWNER_FINANCE_MAX_MONTHS).default(12),
  })
  .refine((q) => !q.start_date || !q.end_date || q.start_date <= q.end_date, {
    message: "start_date must be on or before end_date",
    path: ["end_date"],
  });

export type OwnerPortalFinanceQuery = z.infer<typeof ownerPortalFinanceQuerySchema>;

// ─── Owner record ────────────────────────────────────────────────────────────

/**
 * `GET /owner-portal/me` — the caller's owner record in the active company
 * (the newest active row linked to their user). `linked_owner_ids` lists every
 * owner row of the same person in the company (the fragmented-person model);
 * owner-portal reads are scoped to all of them.
 */
export const ownerPortalOwnerSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    owner_type: z.string(),
    full_name: z.string(),
    full_name_en: z.string().nullable(),
    commercial_name: z.string().nullable(),
    commercial_name_en: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    /** Last 4 digits of the national id / unified number, or null. */
    national_id_last4: z.string().nullable(),
    invitation_status: z.string(),
    created_at: z.string(),
    linked_owner_ids: z.array(z.string()),
  })
  .passthrough();

export type OwnerPortalOwner = z.infer<typeof ownerPortalOwnerSchema>;

// ─── Finance summary ─────────────────────────────────────────────────────────

export const ownerPortalFinanceTotalsSchema = z
  .object({
    revenue: z.number(),
    expenses: z.number(),
    net: z.number(),
  })
  .passthrough();

export const ownerPortalFinanceMonthSchema = z
  .object({
    /** `YYYY-MM`. */
    month: z.string(),
    revenue: z.number(),
    expenses: z.number(),
    net: z.number(),
  })
  .passthrough();

/**
 * `GET /owner-portal/finance-summary` — revenue (receipts), expenses
 * (`total_amount`, VAT included) and net for the requested range, plus a
 * trailing monthly series, oldest month first. Scoped to the owner person's
 * contracts and properties in the active company.
 */
export const ownerPortalFinanceSummarySchema = z
  .object({
    currency: z.literal("SAR"),
    /** Today in Asia/Riyadh, `YYYY-MM-DD`. */
    today: z.string(),
    range: z
      .object({ start_date: z.string().nullable(), end_date: z.string().nullable() })
      .passthrough(),
    totals: ownerPortalFinanceTotalsSchema,
    monthly: z.array(ownerPortalFinanceMonthSchema),
  })
  .passthrough();

export type OwnerPortalFinanceTotals = z.infer<typeof ownerPortalFinanceTotalsSchema>;
export type OwnerPortalFinanceMonth = z.infer<typeof ownerPortalFinanceMonthSchema>;
export type OwnerPortalFinanceSummary = z.infer<typeof ownerPortalFinanceSummarySchema>;

// ─── Properties & units (mobile gap B10) ─────────────────────────────────────

/** Days before `end_date` at which a rented unit counts as expiring soon. */
export const OWNER_EXPIRING_WINDOW_DAYS = 60;

/**
 * Display status of one unit, derived on every read (precedence top → bottom):
 * - `late_rent`   — the owner's active contract has an unpaid instalment past
 *                   due (a transfer proof awaiting verification does not count);
 * - `expiring`    — the owner's active contract ends within
 *                   {@link OWNER_EXPIRING_WINDOW_DAYS} days;
 * - `rented`      — an active contract on the unit, or `units.status = rented`;
 * - `maintenance` / `reserved` / `sold` — `units.status`, when not rented;
 * - `marketing`   — vacant with an active listing;
 * - `vacant`      — everything else.
 */
export const ownerUnitDisplayStatusSchema = z.enum([
  "late_rent",
  "expiring",
  "rented",
  "maintenance",
  "reserved",
  "sold",
  "marketing",
  "vacant",
]);

export type OwnerUnitDisplayStatus = z.infer<typeof ownerUnitDisplayStatusSchema>;

/**
 * Unit counts for one property, or the whole portfolio. `rented` counts every
 * occupied unit (late and expiring ones included); `vacant` includes the
 * `marketing` ones. `occupancy_rate` is `rented / unit_count` as a whole
 * percentage (0 when there are no units).
 */
export const ownerPortalOccupancySchema = z
  .object({
    unit_count: z.number(),
    rented: z.number(),
    vacant: z.number(),
    marketing: z.number(),
    maintenance: z.number(),
    reserved: z.number(),
    sold: z.number(),
    late_rent: z.number(),
    expiring_soon: z.number(),
    occupancy_rate: z.number(),
  })
  .passthrough();

export type OwnerPortalOccupancy = z.infer<typeof ownerPortalOccupancySchema>;

/** One property card on عقاراتي. `image_url` is the first property image. */
export const ownerPortalPropertyCardSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    name_en: z.string().nullable(),
    property_type: z.string(),
    purpose: z.string().nullable(),
    city: z.string().nullable(),
    city_en: z.string().nullable(),
    district: z.string().nullable(),
    district_en: z.string().nullable(),
    address: z.string().nullable(),
    address_en: z.string().nullable(),
    image_url: z.string().nullable(),
    occupancy: ownerPortalOccupancySchema,
  })
  .passthrough();

export type OwnerPortalPropertyCard = z.infer<typeof ownerPortalPropertyCardSchema>;

export const ownerPortalPortfolioTotalsSchema = ownerPortalOccupancySchema
  .extend({ property_count: z.number() })
  .passthrough();

export type OwnerPortalPortfolioTotals = z.infer<typeof ownerPortalPortfolioTotalsSchema>;

/**
 * `GET /owner-portal/properties` — the owner person's properties in the active
 * company (`properties.owner_id` ∪ properties of the owner's contracts, the
 * same scope as the finance summary), newest first, plus portfolio totals.
 */
export const ownerPortalPropertiesResponseSchema = z
  .object({
    /** Today in Asia/Riyadh, `YYYY-MM-DD`. */
    today: z.string(),
    expiring_window_days: z.number(),
    totals: ownerPortalPortfolioTotalsSchema,
    properties: z.array(ownerPortalPropertyCardSchema),
  })
  .passthrough();

export type OwnerPortalPropertiesResponse = z.infer<typeof ownerPortalPropertiesResponseSchema>;

/** One unit row on the property screen. */
export const ownerPortalUnitSummarySchema = z
  .object({
    id: z.string(),
    property_id: z.string(),
    unit_number: z.string(),
    unit_type: z.string(),
    purpose: z.string().nullable(),
    floor_number: z.number().nullable(),
    area: z.number().nullable(),
    bedrooms: z.number().nullable(),
    bathrooms: z.number().nullable(),
    rent_amount: z.number().nullable(),
    annual_rent: z.number().nullable(),
    is_furnished: z.boolean().nullable(),
    image_url: z.string().nullable(),
    /** Raw `units.status`. */
    unit_status: z.string(),
    status: ownerUnitDisplayStatusSchema,
    /** End date of the owner's active contract on the unit, else null. */
    contract_end_date: z.string().nullable(),
    /** Days from today to `contract_end_date` (negative once past), else null. */
    days_to_end: z.number().nullable(),
  })
  .passthrough();

export type OwnerPortalUnitSummary = z.infer<typeof ownerPortalUnitSummarySchema>;

export const ownerPortalPropertyDetailPropertySchema = ownerPortalPropertyCardSchema
  .extend({
    description: z.string().nullable(),
    description_en: z.string().nullable(),
    total_area: z.number().nullable(),
    built_year: z.number().nullable(),
    floors_count: z.number().nullable(),
    deed_number: z.string().nullable(),
    images: z.array(z.string()),
  })
  .passthrough();

/**
 * One unit row on the property screen: the summary plus every unit image (the
 * gallery) and, when the unit's active contract is the owner's, its tenant.
 */
export const ownerPortalPropertyUnitSchema = ownerPortalUnitSummarySchema
  .extend({
    images: z.array(z.string()),
    tenant_name: z.string().nullable(),
    tenant_name_en: z.string().nullable(),
  })
  .passthrough();

/**
 * Rent collection over the owner's active contracts on the property (the
 * tenant portal's instalment rules — see `summariseInstalments`).
 */
export const ownerPortalPropertyCollectionSchema = z
  .object({
    active_contracts: z.number(),
    total_amount: z.number(),
    collected_amount: z.number(),
    overdue_amount: z.number(),
    overdue_count: z.number(),
  })
  .passthrough();

/** An open maintenance request of the owner's on the property. */
export const ownerPortalPropertyMaintenanceItemSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    status: z.string(),
    priority: z.string(),
    created_at: z.string(),
    unit_number: z.string().nullable(),
  })
  .passthrough();

/** `GET /owner-portal/properties/:id` — one property and its units. */
export const ownerPortalPropertyDetailSchema = z
  .object({
    today: z.string(),
    property: ownerPortalPropertyDetailPropertySchema,
    units: z.array(ownerPortalPropertyUnitSchema),
    collection: ownerPortalPropertyCollectionSchema,
    open_maintenance: z.array(ownerPortalPropertyMaintenanceItemSchema),
  })
  .passthrough();

export type OwnerPortalPropertyDetailProperty = z.infer<
  typeof ownerPortalPropertyDetailPropertySchema
>;
export type OwnerPortalPropertyUnit = z.infer<typeof ownerPortalPropertyUnitSchema>;
export type OwnerPortalPropertyCollection = z.infer<typeof ownerPortalPropertyCollectionSchema>;
export type OwnerPortalPropertyMaintenanceItem = z.infer<
  typeof ownerPortalPropertyMaintenanceItemSchema
>;
export type OwnerPortalPropertyDetail = z.infer<typeof ownerPortalPropertyDetailSchema>;

/** The tenant of a rented unit, as the owner sees them (id number masked). */
export const ownerPortalUnitTenantSchema = z
  .object({
    id: z.string(),
    tenant_type: z.string(),
    name: z.string(),
    name_en: z.string().nullable(),
    /** Last 4 characters of the id number, or null. The full number never leaves the API. */
    national_id_last4: z.string().nullable(),
    /** Start of the tenant's first contract with the owner on this unit, `YYYY-MM-DD`. */
    tenant_since: z.string(),
    /** Whole months from `tenant_since` to today. */
    tenure_months: z.number(),
  })
  .passthrough();

/** The owner's active contract on a rented unit, with collection totals. */
export const ownerPortalUnitContractSchema = z
  .object({
    id: z.string(),
    contract_number: z.string().nullable(),
    ejar_number: z.string().nullable(),
    start_date: z.string(),
    end_date: z.string(),
    days_to_end: z.number(),
    payment_frequency: z.string().nullable(),
    payments_count: z.number().nullable(),
    rent_amount: z.number().nullable(),
    /** Sum of the contract's instalments (cancelled ones excluded). */
    total_amount: z.number(),
    collected_amount: z.number(),
    remaining_amount: z.number(),
    overdue_amount: z.number(),
    overdue_count: z.number(),
    next_due_date: z.string().nullable(),
    next_due_amount: z.number().nullable(),
  })
  .passthrough();

/** One activity-feed entry on the unit screen, newest first. */
export const ownerPortalUnitActivitySchema = z
  .object({
    type: z.enum(["payment_received", "maintenance_request", "contract_started"]),
    /** `YYYY-MM-DD` for payments and contract starts, ISO timestamp for maintenance. */
    date: z.string(),
    id: z.string(),
    amount: z.number().nullable(),
    title: z.string().nullable(),
    status: z.string().nullable(),
    reference: z.string().nullable(),
  })
  .passthrough();

export type OwnerPortalUnitTenant = z.infer<typeof ownerPortalUnitTenantSchema>;
export type OwnerPortalUnitContract = z.infer<typeof ownerPortalUnitContractSchema>;
export type OwnerPortalUnitActivity = z.infer<typeof ownerPortalUnitActivitySchema>;

export const ownerPortalUnitDetailUnitSchema = ownerPortalUnitSummarySchema
  .extend({
    living_rooms: z.number().nullable(),
    description: z.string().nullable(),
    description_en: z.string().nullable(),
    images: z.array(z.string()),
    utilities: z
      .object({
        electricity_meter: z.string().nullable(),
        water_meter: z.string().nullable(),
      })
      .passthrough(),
  })
  .passthrough();

export type OwnerPortalUnitDetailUnit = z.infer<typeof ownerPortalUnitDetailUnitSchema>;

/**
 * Vacancy stats of an unoccupied unit (mobile F05), from the owner person's
 * own ended contracts on it. Every field is null when unknown.
 */
export const ownerPortalUnitVacancySchema = z
  .object({
    /** When the last tenant left, `YYYY-MM-DD`: the latest ended contract's end. */
    vacant_since: z.string().nullable(),
    /** Whole days from `vacant_since` to today. */
    vacant_days: z.number().nullable(),
    /** Mean tenant stay in months, renewals joined into one stay, 1 dp. */
    average_stay_months: z.number().nullable(),
    /** `rent_amount` of the latest ended contract (same meaning as `contract.rent_amount`). */
    last_rent: z.number().nullable(),
    /** Asking price of the unit's active rent listing. */
    suggested_rent: z.number().nullable(),
  })
  .passthrough();

export type OwnerPortalUnitVacancy = z.infer<typeof ownerPortalUnitVacancySchema>;

/** Where an unoccupied unit is in marketing; the furthest stage reached wins. */
export const ownerUnitMarketingStageSchema = z.enum([
  "photography",
  "listed",
  "viewings",
  "contracting",
]);

export type OwnerUnitMarketingStage = z.infer<typeof ownerUnitMarketingStageSchema>;

/** How far a prospective tenant got, lowest to highest. */
export const ownerUnitApplicantStatusSchema = z.enum([
  "inquired",
  "viewing_scheduled",
  "viewed",
  "very_interested",
  "contract_requested",
]);

export type OwnerUnitApplicantStatus = z.infer<typeof ownerUnitApplicantStatusSchema>;

/**
 * One prospective tenant, merged by phone across the listing's leads, the
 * unit's applications, viewings and contract requests. Phone and id number
 * leave the API as their last 4 digits only.
 */
export const ownerPortalUnitApplicantSchema = z
  .object({
    name: z.string(),
    status: ownerUnitApplicantStatusSchema,
    /** ISO timestamp of the latest inquiry, viewing or request. */
    last_activity_at: z.string(),
    phone_last4: z.string().nullable(),
    national_id_last4: z.string().nullable(),
  })
  .passthrough();

export type OwnerPortalUnitApplicant = z.infer<typeof ownerPortalUnitApplicantSchema>;

/** Marketing status of an unoccupied unit (mobile F06). */
export const ownerPortalUnitMarketingSchema = z
  .object({
    stage: ownerUnitMarketingStageSchema,
    /** ISO timestamp the current marketing round started; counts and applicants run from it. */
    started_at: z.string(),
    /** ISO timestamp the active listing went live, or null while there is none. */
    listed_at: z.string().nullable(),
    counts: z
      .object({
        /** Viewings booked (cancelled ones excluded). */
        viewing_requests: z.number(),
        completed_viewings: z.number(),
        /** Completed viewings rated `very_interested`. */
        very_interested: z.number(),
      })
      .passthrough(),
    applicant_count: z.number(),
    /** Up to 5, furthest status first, then most recent. */
    applicants: z.array(ownerPortalUnitApplicantSchema),
  })
  .passthrough();

export type OwnerPortalUnitMarketing = z.infer<typeof ownerPortalUnitMarketingSchema>;

/**
 * `GET /owner-portal/units/:id` — unit details. `tenant` and `contract` are
 * set only when the unit's active contract is the owner's own (another owner's
 * tenant is never shown); `activity` lists recent receipts on the unit,
 * the owner's maintenance requests on it, and the contract start. `vacancy`
 * is set whenever the unit is unoccupied; `marketing` only while it is being
 * marketed (an active listing, or a marketing step of the owner's service
 * request).
 */
export const ownerPortalUnitDetailSchema = z
  .object({
    today: z.string(),
    property: z
      .object({
        id: z.string(),
        name: z.string(),
        name_en: z.string().nullable(),
        city: z.string().nullable(),
        district: z.string().nullable(),
      })
      .passthrough(),
    unit: ownerPortalUnitDetailUnitSchema,
    tenant: ownerPortalUnitTenantSchema.nullable(),
    contract: ownerPortalUnitContractSchema.nullable(),
    activity: z.array(ownerPortalUnitActivitySchema),
    vacancy: ownerPortalUnitVacancySchema.nullable(),
    marketing: ownerPortalUnitMarketingSchema.nullable(),
  })
  .passthrough();

export type OwnerPortalUnitDetail = z.infer<typeof ownerPortalUnitDetailSchema>;
